<template>
    <div ref="rangeSlider" class="range_slider" @click="clicked">
        <div class="range_line">
            <div ref="lineSelect" class="line_select">
                <span ref="nodeLeft" class="node_left_range" @mousedown="mouseMove"></span>
                <span ref="nodeRight" class="node_right_range" @mousedown="mouseMove"></span>
            </div>
            <RangeNodes
                v-if="showNodes && rangeSlider != null"
                :bg="nodeColor"
                :count="nodeCount"
                :line-color="lineColor"
                :select-node="selected"
                :size="nodeSize"
                :text-color="colorTextPercent"
                :transition="transition"
                :place-node-left="placeNodeLeft"
                :width="rangeSlider.getBoundingClientRect().width"
                :place-node-right="placeNodeRight"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from "vue";
import RangeNodes from "./rangeNodes";

const props = withDefaults(
    defineProps<{
        width: string | number,
        colorTextPercent?: string,
        nodeSize?: number,
        nodeCount?: number,
        nodeColor?: string,
        lineColor?: string,
        defaultPercentage?: number,
        transition?: number,
        attractionUnit?: number,
        showNodes?: boolean,
        showPercentage?: boolean,
        defaultRightNode?: number,
        defaultLeftNode?: number
    }>(),
    {
        width: 320,
        colorTextPercent: "black",
        nodeSize: 16,
        nodeCount: 10,
        nodeColor: "var(--gray-300)",
        lineColor: "var(--identity)",
        defaultPercentage: 10,
        transition: 0.2,
        attractionUnit: 5,
        showNodes: true,
        showPercentage: false,
        defaultRightNode: 100,
        defaultLeftNode: 0
    }
);

const emits = defineEmits(['leftRange', 'rightRange', 'rangeFinalizer'])

enum Drag {
    Touch = "Touch",
    Click = "Click",
    MOVE = "MOVE",
}

const selected = ref<number>(props.defaultPercentage);
const rangeSlider = ref<HTMLDivElement | null>(null)
const nodeLeft = ref<HTMLDivElement | null>(null)
const nodeRight = ref<HTMLDivElement | null>(null)
const lineSelect = ref<HTMLElement | null>(null)
const typeDrag = ref<Drag>(Drag.Click)
const placeNodeLeft = ref<number>(props.defaultLeftNode)
const placeNodeRight = ref<number>(props.defaultRightNode)

onMounted(() => {
    const cssWidth = typeof props.width == "string" ? props.width : props.width + "px";
    if (rangeSlider.value !== null) {
        rangeSlider.value.style.width = cssWidth;
        rangeSlider.value.addEventListener("mousedown", () => {
            window.document.body.style.userSelect = 'none'
            window.onmouseup = mouseUp
        })
        rangeSlider.value.addEventListener("touchmove", function (event) {
            touchMove(event);
            window.document.body.style.userSelect = 'none'
            window.ontouchend = touchEnd
            document.getElementsByTagName('body')[0].style.overflow = 'hidden';
        });
    }
});

function mouseupDragElement() {
    document.onmousemove = null
}

function mouseUp() {
    mouseupDragElement();
    emitData()
    rangeFinalizer();
    window.document.body.style.userSelect = 'unset'
    window.onmouseup = null
}

function touchEnd() {
    emitData()
    rangeFinalizer();
    window.document.body.style.userSelect = 'unset'
    document.getElementsByTagName('body')[0].style.overflow = 'auto';
    window.ontouchend = null
}

function mouseMove() {
    typeDrag.value = Drag.MOVE
    document.onmousemove = eventRangeSlider
}

function clicked(e: MouseEvent) {
    typeDrag.value = Drag.Click
    eventRangeSlider(e)
}

function touchMove(e: TouchEvent) {
    typeDrag.value = Drag.Touch
    eventRangeSlider(e)
}

function rangeFinalizer() {
    if (rangeSlider.value !== null) {
        setTimeout(() => {
            emits('rangeFinalizer');
        }, 200)
    }
}

function eventRangeSlider(e: MouseEvent | TouchEvent) {
    let placeNodeLeft: number
    let placeNodeRight: number
    let lineStart: number
    let lineEnd: number
    let placeMouse: number

    if (rangeSlider.value !== null && nodeRight.value !== null && nodeLeft.value !== null && lineSelect.value !== null) {
        lineStart = rangeSlider.value.getBoundingClientRect().left;
        lineEnd = rangeSlider.value.getBoundingClientRect().width;
        placeNodeLeft = (nodeLeft.value.getBoundingClientRect().left - lineStart) + (props.nodeSize / 2);
        placeNodeRight = (nodeRight.value.getBoundingClientRect().left - lineStart) + (props.nodeSize / 2);

        if ('targetTouches' in e && typeDrag.value === Drag.Touch) {
            placeMouse = e.targetTouches[0].clientX - lineStart;
        } else if ('clientX' in e) {
            placeMouse = e.clientX - lineStart;
        } else return;

        if (placeMouse > lineEnd || placeMouse <= 0) {
            return
        }


        if (placeMouse <= ((placeNodeRight + placeNodeLeft) / 2)) {
            placeMouse = (Number(Math.floor(magnetNode((placeMouse))) * lineEnd) / 100)
            lineSelect.value.style.left = placeMouse + 'px'
            lineSelect.value.style.width = Number(lineSelect.value.getBoundingClientRect().width) - Number((placeMouse - placeNodeLeft)) + 'px'
        } else {
            placeMouse = (Number(Math.floor(magnetNode((placeMouse))) * lineEnd) / 100)
            if ((placeMouse - placeNodeLeft) < 1) return;
            lineSelect.value.style.width = (placeMouse - placeNodeLeft) + 'px'
        }
        nodesActive((((nodeLeft.value.getBoundingClientRect().left - lineStart) + (props.nodeSize / 2)) / lineEnd) * 100, (((nodeRight.value.getBoundingClientRect().left - lineStart) + (props.nodeSize / 2)) / lineEnd) * 100)
    }
}

function magnetNode(nodeLocation: number) {
    if (rangeSlider.value == null) {
        return 0;
    }

    const nodePercent = (nodeLocation / rangeSlider.value.getBoundingClientRect().width) * 100

    const space = 100 / props.nodeCount

    for (let i = 0; i <= 100; i += space) {
        if (nodePercent > i && nodePercent < i + space) {
            if (((i + i + space) / 2) <= nodePercent) {
                return i + space
            } else {
                return i
            }
        }
    }

    return nodePercent
}

function nodesActive(nodeLeft: number, nodeRight: number) {
    placeNodeLeft.value = nodeLeft
    placeNodeRight.value = Math.ceil(nodeRight)
}

function emitData() {
    setTimeout(() => {
        emits('leftRange', Math.floor(placeNodeLeft.value));
        emits('rightRange', Math.ceil(placeNodeRight.value));
    }, 100)
}

</script>
<style scoped>
.range_slider {
    position: relative;
    direction: ltr;
    padding: 10px 0;
}

.range_line {
    background-color: var(--gray-300);
    display: block;
    height: 2px;
}

.line_select {
    height: 100%;
    width: v-bind(defaultRightNode+ '%');
    background-color: var(--identity);
    position: relative;
    display: flex;
    align-items: center;
}

.node_right_range {
    display: flex;
    align-items: flex-end;
    position: absolute;
    right: v-bind(-nodeSize / 2 + 'px');
    border-radius: 150px;
    z-index: 2
}

.node_left_range {
    display: flex;
    align-items: flex-end;
    position: absolute;
    left: v-bind(-nodeSize / 2 + 'px');
    border-radius: 150px;
    z-index: 1;
}

.node_right_range, .node_left_range {
    width: v-bind(nodeSize+ 'px');
    height: v-bind(nodeSize+ 'px');
    color: var(--identity);
    transform: rotate(90deg);
    background-color: var(--identity);
}

body[data-theme='light'] .percentage {
    color: var(--gray-500);
}

body[data-theme='light'] .range_line {
    background-color: var(--gray-200) !important;
}

:deep(.seekbar__subtext__dark) {
    color: var(--gray-200);
}

:deep(.seekbar__subtext__light) {
    color: var(--gray-400);
}
</style>